import { useState } from "react";
import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import { useToast } from "../../contexts/ToastContext";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import type { ApiMessage, SelectOption } from "../../types/api";
import type {
  AddOrderProductPayload, OrderItemRecord, OrderRecord, ProductDropdownOption,
  UpdateOrderItemPayload, UpdateOrderPayload,
} from "../../types/orders";
import type { EmptyModalState } from "../../components/modals/EmptyModal";
import type { SlideOverState } from "../../components/slideOver/SlideOver";
import { getSalesOrderTitle } from "../../shared/orderNumbers";
import {
  downloadOrderInvoicePdf, getOrderInvoicePdf, OrderInvoiceApiError,
} from "../../shared/orderInvoiceApi";

interface OrderTotals {
  subtotal_dollar_amount: number;
  dollar_tax_amount: number;
  total_dollar_amount: number;
}
interface UpdateItemResponse extends ApiMessage { order: OrderTotals }
interface DeleteItemResponse extends ApiMessage { order_item: OrderItemRecord }

export function OrderDetailsViewModel(orderId: string | undefined, logout: () => void) {
  const { showSuccess } = useToast();
  const [customers, setCustomers] = useState<{ customers: SelectOption[] }>({ customers: [] });
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([]);
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({ open: false, childComponent: null });
  const [order, setOrder] = useState<OrderRecord | null>(null);
  const [loading] = useState(false);
  const [downloadingEstimate, setDownloadingEstimate] = useState(false);
  const [slideOver, setSlideOver] = useState<SlideOverState>({ childComponent: null, open: false });
  const closeModal = () => setEmptyModelOpen({ childComponent: null, open: false });
  const showErrorModal = (title = "Unable to complete request", subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: <ModelErrorComponent setOpen={closeModal} subtitle={subtitle} title={title} />,
      open: true,
    });
  };
  const unauthorized = (status?: number) => { if (status === 401) logout(); };

  const getOrderDetails = async () => {
    if (!orderId) return;
    const result = await makePostApiCall<OrderRecord>(`fetchOrder/${orderId}`);
    if (result.success) setOrder(result.data);
    else { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
  };

  const getOrderTotalsOnly = async () => {
    if (!orderId) return makeGetApiCall<OrderTotals>("getOrderTotalsOnly/0");
    const result = await makeGetApiCall<OrderTotals>(`getOrderTotalsOnly/${orderId}`);
    if (!result.success) { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
    return result;
  };

  const getCustomersForDropdown = async () => {
    const result = await makePostApiCall<{ customers: SelectOption[] }>("getCustomersForDropdown");
    if (result.success) setCustomers(result.data);
    else { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
    return result;
  };

  const getMyProducts = async (search: string): Promise<ProductDropdownOption[]> => {
    if (!order) return [];
    const payload = { customer_id: order.customer_id, search };
    const result = await makePostApiCall<ProductDropdownOption[], typeof payload>("fetchProductsForDropdown", payload);
    if (result.success) return result.data;
    unauthorized(result.errorStatus);
    showErrorModal(result.data.message);
    return [];
  };

  const updateOrderApiCall = async (payload: UpdateOrderPayload) => {
    const result = await makePostApiCall<{ message: string; order: OrderRecord }, UpdateOrderPayload>(
      `updateOrder/${orderId ?? 0}`, payload,
    );
    if (result.success) showSuccess(result.data.message || "Action completed");
    else { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
    return result;
  };

  const onUpdateOrderItem = async (payload: UpdateOrderItemPayload) => {
    const result = await makePostApiCall<UpdateItemResponse, Omit<UpdateOrderItemPayload, "id">>(
      `updateOrderItem/${payload.id}`, payload,
    );
    if (result.success) showSuccess(result.data.message || "Action completed");
    else { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
    return result;
  };

  const deleteOrderItemApiCall = async (id: number) => {
    const result = await makePostApiCall<DeleteItemResponse>(`deleteOrderItem/${id}`);
    if (result.success) { closeModal(); showSuccess(result.data.message || "Action completed"); }
    else { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
    return result;
  };

  const addProductToOrderApiCall = async (targetOrderId: number, payload: AddOrderProductPayload) => {
    const result = await makePostApiCall<OrderItemRecord, AddOrderProductPayload>(
      `addProductToOrder/${targetOrderId}`, payload,
    );
    if (result.success) showSuccess("Product added to order");
    else { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
    return result;
  };

  const getUsersForDropdown = async (type?: number, department?: number) => {
    const payload = { department, type };
    const result = await makePostApiCall<SelectOption[], typeof payload>("usersForDropdown", payload);
    if (result.success) setEmployeeOptions(result.data);
    else { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
  };

  const printOrder = async () => {
    try {
      const { blob, filename } = await getOrderInvoicePdf(orderId ?? "0", order?.order_number);
      downloadOrderInvoicePdf(blob, filename);
    } catch (error: unknown) {
      if (error instanceof OrderInvoiceApiError) {
        unauthorized(error.status);
        showErrorModal("Unable to download invoice", error.message);
        return;
      }

      showErrorModal("Unable to download invoice", "Could not generate the invoice PDF.");
    }
  };

  const deleteOrder = async (id: number) => {
    const result = await makePostApiCall<ApiMessage>(`/deleteOrder/${id}`);
    if (result.success) { closeModal(); window.location.href = "/orders"; }
    else { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
  };

  const askDeleteOrder = (selectedOrder: OrderRecord) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteOrder(selectedOrder.id)}
          message={`Are you sure you want to delete ${getSalesOrderTitle(selectedOrder)}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const downloadEstimate = async () => {
    if (downloadingEstimate) return;
    setDownloadingEstimate(true);
    try {
      const result = await makePostApiCall<{ pdf: string; filename: string }>(
        `readEstimateFromQb/${orderId ?? 0}`, { include_metadata: true },
      );
      if (result.success) {
        const binary = window.atob(result.data.pdf);
        const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
        const link = document.createElement("a");
        const url = window.URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
        link.href = url;
        link.download = result.data.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
    } catch {
      showErrorModal("Unable to download estimate", "Please try again.");
    } finally {
      setDownloadingEstimate(false);
    }
  };

  return {
    customers, getCustomersForDropdown, getMyProducts, loading, setEmptyModelOpen, setSlideOver,
    slideOver, emptyModal, showErrorModal, updateOrderApiCall, order, getOrderDetails,
    onUpdateOrderItem, deleteOrderItemApiCall, addProductToOrderApiCall, employeeOptions,
    getUsersForDropdown, printOrder, askDeleteOrder, downloadEstimate, downloadingEstimate,
    getOrderTotalsOnly,
  };
}
